import React, { useMemo } from 'react'
import { Eye, PenLine, EyeOff, Pencil } from 'lucide-react'
import { Card } from '../ui/Card'
import type { UserType } from '../../types/exchange'
import type { FeatureKey } from '../../lib/permissions'
import { FEATURE_LABELS, DEFAULT_FEATURE_PERMISSIONS } from '../../lib/permissions'
import { roleLabel } from '../../lib/roleFormatting'
import type { ExtendedUserType } from './UserTypeSelector'
import type { PermissionDisplayLevel } from './UserPermissionsCard'

function featurePermissionsToDisplayLevel(
  fp: Record<string, unknown>,
  feature: FeatureKey,
): PermissionDisplayLevel {
  const raw = fp[feature] as unknown
  if (!raw || (Array.isArray(raw) && (raw as unknown[]).length === 0)) {
    return 'hidden'
  }
  if (Array.isArray(raw)) {
    const hasWrite = (raw as string[]).some((action) =>
      ['upload', 'sign', 'fill', 'generate', 'use', 'browse', 'view'].includes(action)
    )
    return hasWrite ? 'read_write' : 'read'
  }
  return 'read_write'
}

const LEVEL_LABEL: Record<PermissionDisplayLevel, string> = {
  read: 'Read',
  read_write: 'Read+Write',
  hidden: 'Hidden',
}

const LEVEL_ICON: Record<PermissionDisplayLevel, React.ElementType> = {
  read: Eye,
  read_write: PenLine,
  hidden: EyeOff,
}

const LEVEL_COLOR: Record<PermissionDisplayLevel, string> = {
  read: 'text-blue-600 dark:text-blue-400',
  read_write: 'text-emerald-600 dark:text-emerald-400',
  hidden: 'text-muted-foreground',
}

interface UserPermissionsSummaryProps {
  userType: ExtendedUserType
  onCustomize: () => void
}

export const UserPermissionsSummary: React.FC<UserPermissionsSummaryProps> = ({
  userType,
  onCustomize,
}) => {
  const allFeatures = Object.keys(FEATURE_LABELS) as FeatureKey[]

  const levels = useMemo(() => {
    const presetPermissions = userType !== 'custom'
      ? DEFAULT_FEATURE_PERMISSIONS[userType as UserType] ?? {}
      : {}
    const result = {} as Record<FeatureKey, PermissionDisplayLevel>
    for (const feature of allFeatures) {
      result[feature] = featurePermissionsToDisplayLevel(presetPermissions as Record<string, unknown>, feature)
    }
    return result
  }, [userType])

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-foreground">
          Preset for {roleLabel(userType)}
        </h3>
        <button
          type="button"
          onClick={onCustomize}
          className="flex items-center gap-1 text-xs text-primary hover:underline"
          aria-label="Customize permissions"
        >
          <Pencil className="h-3 w-3" />
          Customize
        </button>
      </div>
      <div className="flex flex-col gap-1.5">
        {allFeatures.map((feature) => {
          const level = levels[feature]
          const LevelIcon = LEVEL_ICON[level]
          return (
            <div
              key={feature}
              className="flex items-center justify-between text-xs"
            >
              <span className="text-muted-foreground">{FEATURE_LABELS[feature]}</span>
              <span className={`flex items-center gap-1 font-medium ${LEVEL_COLOR[level]}`}>
                <LevelIcon className="h-3 w-3" aria-hidden="true" />
                {LEVEL_LABEL[level]}
              </span>
            </div>
          )
        })}
      </div>
    </Card>
  )
}
